/**
 * Serviço de Alunos
 *
 * Gerencia operações CRUD de alunos usando PostgREST.
 * Seguindo a arquitetura de microserviços, CRUD simples vai para PostgREST.
 */

import { postgrestClient } from "../lib/postgrest/client";
import { postgrestClient as apiClient, apiServiceClient } from "../config/api";
import { registrarFalha } from "../lib/telemetria";

/**
 * Serviço de alunos
 */
export const alunosService = {
  /**
   * Lista alunos com filtros e paginação via PostgREST.
   * Não usa /v1/alunos (cache Redis): esse endpoint omite `bairro` e aliasa
   * cpf/data de nascimento, o que esvazia colunas da tabela de gestão.
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { nucleo_id: "eq.123" })
   * @param {string} options.order - Ordenação (ex: "nome.asc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, order = "nome.asc", limit, offset, includeCount = false } = options;

    const result = await postgrestClient.get("alunos", {
      filters,
      order,
      limit,
      offset,
      includeCount,
    });

    // Se includeCount, retorna { data, total }, senão retorna array direto
    if (includeCount && result && typeof result === 'object' && 'data' in result) {
      return result;
    }
    return result;
  },

  /**
   * Busca aluno por ID
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("alunos", {
      filters: { id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Busca aluno por CPF ou Certidão
   * Usa função RPC que bypassa RLS para permitir verificação de duplicatas
   * @param {string} cpfOuCertidao - CPF ou número da certidão
   * @returns {Promise<Object|null>} - Retorna o aluno encontrado ou null
   */
  async getByCpfOuCertidao(cpfOuCertidao) {
    if (!cpfOuCertidao || !cpfOuCertidao.trim()) {
      return null;
    }

    const cleaned = cpfOuCertidao.replace(/\D/g, "");
    if (cleaned.length < 3) {
      return null;
    }

    try {
      // Usa função RPC que bypassa RLS e busca por CPF/Certidão
      // A função remove formatação automaticamente
      const result = await postgrestClient.rpc("buscar_aluno_por_cpf", {
        p_cpf_ou_certidao: cpfOuCertidao,
      });

      // RPC retorna array ou objeto único
      if (Array.isArray(result) && result.length > 0) {
        return result[0];
      }
      if (result && result.id) {
        return result;
      }
      return null;
    } catch (error) {
      console.error("[alunosService] Erro ao buscar aluno por CPF:", error);
      return null;
    }
  },

  /**
   * Cria novo aluno
   * Usa função RPC criar_aluno que bypassa RLS
   * @param {Object} alunoData
   * @returns {Promise<Object>}
   */
  async create(alunoData) {
    // Mapear campos do payload para parâmetros da função RPC
    const rpcParams = {
      p_nome: alunoData.nome,
      p_sexo: alunoData.sexo || null,
      p_data_nasc: alunoData.data_nasc || null,
      p_cpf_ou_certidao: alunoData.cpf_ou_certidao || null,
      p_escola_origem: alunoData.escola_origem || null,
      p_escola_id: alunoData.escola_id || null,
      p_serie_escolar: alunoData.serie_escolar || null,
      p_turno_escola: alunoData.turno_escola || null,
      p_bairro: alunoData.bairro || null,
      p_responsavel: alunoData.responsavel || null,
      p_telefone_responsavel: alunoData.telefone_responsavel || null,
      p_responsavel_email: alunoData.responsavel_email || null,
      p_endereco_rua: alunoData.endereco_rua || null,
      p_endereco_numero: alunoData.endereco_numero || null,
      p_endereco_complemento: alunoData.endereco_complemento || null,
      p_endereco_cep: alunoData.endereco_cep || null,
      p_neurodivergente_ou_pcd: alunoData.neurodivergente_ou_pcd || false,
      p_deficiencias_especificas: alunoData.deficiencias_especificas || null,
      p_possui_alergia_alimentar: alunoData.possui_alergia_alimentar || false,
      p_pontuacao_calcado: alunoData.pontuacao_calcado || null,
      p_tamanho_uniforme: alunoData.tamanho_uniforme || null,
      p_observacoes: alunoData.observacoes || null,
    };

    // Remover parâmetros null/undefined para não enviar
    Object.keys(rpcParams).forEach(key => {
      if (rpcParams[key] === null || rpcParams[key] === undefined) {
        delete rpcParams[key];
      }
    });

    // Chamar função RPC (retorna objeto completo do aluno)
    const result = await postgrestClient.rpc("criar_aluno", rpcParams);

    // A função RPC retorna o objeto completo do aluno
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza aluno
   * @param {string} id
   * @param {Object} alunoData
   * @returns {Promise<Object>}
   */
  async update(id, alunoData) {
    const result = await postgrestClient.patch("alunos", { id }, alunoData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Remove aluno
   * @param {string} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    return postgrestClient.delete("alunos", { id });
  },

  /**
   * Desmatricula um aluno (encerra matrículas e vínculos sem excluir registros)
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async desmatricular(id) {
    const result = await postgrestClient.rpc("desmatricular_aluno", {
      p_aluno_id: id,
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Exclui definitivamente um aluno e todos os seus vínculos (matrículas,
   * frequências e avaliações). Ação irreversível, restrita a ADMIN/ADMINISTRATIVO
   * (checagem feita no backend, dentro da função RPC).
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async excluirDefinitivo(id) {
    const result = await postgrestClient.rpc("excluir_aluno_definitivo", {
      p_aluno_id: id,
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Busca para matrícula: acha qualquer aluno, só com dados de identificação e
   * CPF mascarado. Só GESTOR, ADMIN, ADMINISTRATIVO e TECNICO (doc 08, S20).
   * @param {string} termo
   * @returns {Promise<Array>} [{id, nome, data_nasc, cpf_mascarado, escola_origem, serie_escolar, bairro, matricula_ativa_no_tenant}]
   */
  async buscarParaMatricula(termo, options = {}) {
    const params = new URLSearchParams({
      termo: String(termo || "").trim(),
      limit: String(Math.min(options.limit || 50, 100)),
      offset: String(options.offset || 0),
    });
    const response = await apiServiceClient.request(`/v1/alunos/busca-matricula?${params.toString()}`, { method: "GET" });
    const alunos = response?.data || response;
    return Array.isArray(alunos) ? alunos : [];
  },

  /**
   * Registro completo de um único aluno, o escolhido para matricular.
   * @param {string} alunoId
   */
  async dadosParaMatricula(alunoId) {
    const response = await apiServiceClient.request(`/v1/alunos/${encodeURIComponent(alunoId)}/dados-matricula`, { method: "GET" });
    return response?.data || response;
  },

  /**
   * Alunos com matrícula ativa nos núcleos informados, com as turmas abertas.
   * O backend só considera os núcleos que o usuário pode ver (doc 08, S20).
   * Se falhar, cai na RPC antiga buscar_alunos_nucleo, com a falha registrada.
   * @param {string[]} nucleoIds
   * @returns {Promise<Array>} [{aluno: {...}, nucleoId, nucleoNome, turmas, mpId, matriculaId, anoId}]
   */
  async porNucleo(nucleoIds = []) {
    try {
      const params = new URLSearchParams();
      nucleoIds.forEach((id) => params.append("nucleo_id", id));
      const response = await apiServiceClient.request(`/v1/alunos/por-nucleo?${params.toString()}`, { method: "GET" });
      const alunos = response?.data || response;
      if (Array.isArray(alunos)) {
        return alunos;
      }
      throw new Error("Resposta inesperada de /v1/alunos/por-nucleo");
    } catch (erro) {
      registrarFalha("alunos.por_nucleo", erro);
    }
    return postgrestClient.rpc("buscar_alunos_nucleo", { p_nucleo_ids: nucleoIds.length > 0 ? nucleoIds : null });
  },

  /**
   * Busca alunos com termo de busca
   * @param {string} searchTerm
   * @param {Object} options
   * @returns {Promise<Array>}
   */
  async search(searchTerm, options = {}) {
    if (!searchTerm || !searchTerm.trim()) {
      const result = await this.list(options);
      // Garante que retorna array mesmo quando list retorna { data, total }
      if (Array.isArray(result)) {
        return result;
      }
      if (result && typeof result === 'object' && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    }

    // Busca do backend novo: só os alunos que o usuário pode ver (doc 08, S20).
    // Se falhar, cai na RPC antiga — que deixa de existir para a API quando o S20
    // fechar; a falha fica registrada (FALHA_FRONT) para ninguém depender dela.
    try {
      const params = new URLSearchParams({
        termo: searchTerm.trim(),
        limit: String(Math.min(options.limit || 50, 100)),
        offset: String(options.offset || 0),
      });
      const response = await apiServiceClient.request(`/v1/alunos/busca?${params.toString()}`, { method: "GET" });
      const alunos = response?.data || response;
      if (Array.isArray(alunos)) {
        return alunos;
      }
      throw new Error("Resposta inesperada de /v1/alunos/busca");
    } catch (erroBusca) {
      registrarFalha("alunos.busca", erroBusca);
    }

    try {
      const searchParams = {
        p_termo: searchTerm.trim(),
        p_limit: options.limit || 50,
        p_offset: options.offset || 0,
      };



      const result = await postgrestClient.rpc("buscar_alunos", searchParams);



      // Garante que retorna array
      if (Array.isArray(result)) {
        return result;
      }
      if (result && typeof result === 'object' && Array.isArray(result.data)) {
        return result.data;
      }

      // Se retornou um objeto vazio ou null, retorna array vazio
      console.warn("RPC buscar_alunos retornou formato inesperado:", result);
      return [];
    } catch (err) {
      // Fallback: busca simples por nome se RPC não existir
      registrarFalha("alunos.busca_rpc", err);
      console.error("Detalhes do erro RPC:", {
        message: err?.message,
        response: err?.response,
        status: err?.response?.status,
        data: err?.response?.data,
      });

      try {
        // Normaliza o CPF removendo pontos e traços para busca
        const cpfNormalizado = searchTerm.trim().replace(/[.\-]/g, '');
        const isCPF = /^\d{11}$/.test(cpfNormalizado) || /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(searchTerm.trim());

        // Fallback: busca por nome OU CPF usando operador OR do PostgREST
        // Se for CPF, busca tanto com formatação quanto sem formatação
        let orFilter;
        if (isCPF && cpfNormalizado !== searchTerm.trim()) {
          // Busca por nome OU CPF formatado OU CPF sem formatação
          const encodedTerm = encodeURIComponent(searchTerm.trim());
          const encodedCPF = encodeURIComponent(cpfNormalizado);
          orFilter = `or=(nome.ilike.*${encodedTerm}*,cpf_ou_certidao.ilike.*${encodedTerm}*,cpf_ou_certidao.ilike.*${encodedCPF}*)`;
        } else {
          // Busca normal por nome ou CPF
          const encodedTerm = encodeURIComponent(searchTerm.trim());
          orFilter = `or=(nome.ilike.*${encodedTerm}*,cpf_ou_certidao.ilike.*${encodedTerm}*)`;
        }

        // Constrói query string manualmente para incluir o filtro OR
        const queryParams = [orFilter];
        if (options.limit) queryParams.push(`limit=${options.limit}`);
        if (options.offset) queryParams.push(`offset=${options.offset}`);
        if (options.order) queryParams.push(`order=${encodeURIComponent(options.order)}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
        // Usa apiClient diretamente para passar a query string completa com operador OR
        const fallbackResult = await apiClient.request(`alunos${queryString}`, {
          method: 'GET',
        });



        // Garante que retorna array mesmo quando list retorna { data, total }
        if (Array.isArray(fallbackResult)) {
          return fallbackResult;
        }
        if (fallbackResult && typeof fallbackResult === 'object' && Array.isArray(fallbackResult.data)) {
          return fallbackResult.data;
        }
        return [];
      } catch (fallbackErr) {
        registrarFalha("alunos.busca_fallback", fallbackErr);
        console.error("Detalhes do erro fallback:", {
          message: fallbackErr?.message,
          response: fallbackErr?.response,
          status: fallbackErr?.response?.status,
          data: fallbackErr?.response?.data,
        });
        return [];
      }
    }
  },

  /**
   * Lista alunos por núcleo
   * Otimizado usando a view 'alunos_por_nucleo' que já faz os joins necessários
   * 
   * @param {string} nucleoId
   * @param {Object} options
   * @returns {Promise<Array>}
   */
  async listarPorNucleo(nucleoId, options = {}) {
    const { anoId, limit = 1000, offset = 0 } = options;

    try {
      const filters = {
        nucleo_id: `eq.${nucleoId}`,
      };

      if (anoId) {
        filters.ano_id = `eq.${anoId}`;
      }

      // Busca na view
      const result = await postgrestClient.get("alunos_por_nucleo", {
        filters,
        limit,
        offset,
        order: "nome.asc"
      });

      if (!Array.isArray(result)) {
        return [];
      }

      // Mapeia o resultado para o formato esperado pelo frontend (aluno_id -> id)
      // A view retorna aluno_id, mas o frontend espera id
      return result.map(item => ({
        ...item,
        id: item.aluno_id, // Renomeia aluno_id para id
        // Garante compatibilidade dos outros campos
        cpf_ou_certidao: item.cpf_ou_certidao,
        data_nasc: item.data_nasc,
        escola_origem: item.escola_origem,
        responsavel: item.responsavel,
        // Mantém os metadados da matrícula/núcleo caso sejam úteis
        _meta: {
          nucleo_id: item.nucleo_id,
          nucleo_nome: item.nucleo_nome,
          matricula_id: item.matricula_id,
          matricula_status: item.matricula_status,
          ano_id: item.ano_id
        }
      }));

    } catch (err) {
      console.error("Erro ao listar alunos por nucleo (view):", err);
      return [];
    }
  },

  /**
   * Lista alunos matriculados por ano letivo usando RPC function
   * @param {Object} options
   * @param {number} options.anoId - ID do ano letivo (opcional, se não fornecido retorna todos matriculados)
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @returns {Promise<Array>}
   */
  async listarMatriculadosPorAno(options = {}) {
    const { anoId = null, limit = 100, offset = 0 } = options;

    try {
      const rpcParams = {
        p_limit: limit,
        p_offset: offset,
      };

      // Se anoId foi fornecido, adiciona ao parâmetro
      if (anoId !== null && anoId !== undefined) {
        rpcParams.p_ano_id = parseInt(anoId, 10);
      }

      const result = await postgrestClient.rpc("listar_alunos_matriculados", rpcParams);

      // Garante que retorna array
      if (Array.isArray(result)) {
        return result;
      }
      if (result && typeof result === 'object' && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    } catch (err) {
      console.error("Erro ao listar alunos matriculados:", err);
      throw err;
    }
  },

  /**
   * Busca bairros únicos da tabela alunos
   * @returns {Promise<Array<string>>}
   */
  async getBairrosUnicos() {
    try {
      const result = await postgrestClient.rpc("get_bairros_unicos");
      return Array.isArray(result) ? result : [];
    } catch (err) {
      // Fallback: buscar todos e extrair (não recomendado para muitos registros)
      registrarFalha("alunos.bairros_unicos_rpc", err);
      const alunos = await this.list({ limit: 10000 });
      const bairros = [...new Set(alunos.map(a => a.bairro).filter(Boolean))];
      return bairros.sort();
    }
  },
};

export default alunosService;
